import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { isEmpty } from 'ramda';
import * as Sentry from '@sentry/angular';
import { DesignStructure } from '@gelato-api-ui/core/designs/design-structure';
import { PreviewFileType } from '@gelato-api-ui/core/preflight/preview-file-type.enum';
import { callWithRetries } from '@gelato-api-ui/core/api/helpers/callWithRetries';
import { PreflightGraphQlService } from '@gelato-api-ui/core/preflight/services/preflight-graph-ql.service';
import { CachedRequestsService } from '@gelato-api-ui/core/api/services/cached-requests.service';
import { DesignProductPreviewUrlParams } from '@gelato-api-ui/core/preflight/types/design-product-preview-url-params';

@Injectable({ providedIn: 'root' })
export class DesignProductPreviewUrlParamsProvider {
  constructor(
    private readonly preflightGraphQlService: PreflightGraphQlService,
    private readonly cachedRequestsService: CachedRequestsService,
  ) {}

  get(
    productUid: string,
    designStructure: DesignStructure,
    designPreviewFileType: PreviewFileType,
    width: number,
    height: number,
    removeSamples: boolean,
  ): Observable<DesignProductPreviewUrlParams> {
    return this.fetchDesignProductPreviewUrlParams(
      productUid,
      designStructure,
      designPreviewFileType,
      width,
      height,
      removeSamples,
    ).pipe(
      tap(designPreviewUrlParams => {
        if (!designPreviewUrlParams || isEmpty(designPreviewUrlParams)) {
          this.trackEmptyDesignPreviewUrlParams(
            productUid,
            designStructure,
            designPreviewFileType,
            width,
            height,
            removeSamples,
          );
        }
      }),
    );
  }

  private fetchDesignProductPreviewUrlParams(
    productUid: string,
    designStructure: DesignStructure,
    designPreviewFileType: PreviewFileType,
    width: number,
    height: number,
    removeSamples: boolean,
  ): Observable<DesignProductPreviewUrlParams> {
    const requestData = {
      productUid,
      designStructure,
      designPreviewFileType,
      width,
      height,
      removeSamples,
    };

    return this.cachedRequestsService.get('DesignProductPreviewUrlParamsProvider', requestData, () =>
      callWithRetries(() =>
        this.preflightGraphQlService.fetchDesignProductPreviewUrlParams(
          productUid,
          designStructure,
          designPreviewFileType,
          width,
          height,
          removeSamples,
        ),
      ),
    );
  }

  private trackEmptyDesignPreviewUrlParams(
    productUid: string,
    designStructure: DesignStructure,
    designPreviewFileType: PreviewFileType,
    width: number,
    height: number,
    removeSamples: boolean,
  ) {
    Sentry.captureException(new Error('EmptyDesignPreviewUrlParams'), scope => {
      scope.setExtras({
        productUid,
        designStructure: JSON.stringify(designStructure),
        designPreviewFileType,
        width,
        height,
        removeSamples,
      });

      return scope;
    });
  }
}
